/**
 * Learning (Обучение) — corporate course catalog and progress tracker.
 *
 * Tamagui port of the handoff `LearningScreen` (screen-work.jsx), re-skinned to
 * the AS Life brand (navy cover + bronze accent instead of the handoff purple):
 *  • stats strip — courses in progress · completed · total lessons
 *  • "Continue learning" hero — the most-advanced in-progress course, navy
 *    banner with a bronze progress ring
 *  • category filter chips → filtered catalog grid of course cards
 *
 * Live data only: catalog from `GET /learning/courses`, the stats strip from
 * `GET /learning/me/summary`, the Start button enrolls (`POST …/enroll`) and
 * Continue advances progress (`PATCH …/progress`). An empty or unreachable
 * response shows the empty state (no demo fallback).
 */
import { useMemo, useState } from 'react'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp, type Localized } from '../../src/i18n'
import { useResponsive } from '../../src/lib/responsive'
import {
  useCourses,
  useLearningSummary,
  useEnrollCourse,
  useSetCourseProgress,
} from '../../src/features/learning/useLearning'
import { tone, type Tone } from '../../src/theme/tones'
import { palette } from '../../src/theme/palette'
import { Button, Card, Enter, Icon, PressCard, ProgressBar, QueryState, Ring, Tag } from '../../src/ui'
import { Page } from '../../src/ui/Screen'

const NAVY = palette.coverNavy
const BLOOM = palette.navy
const BRONZE = palette.bronzeBrushed
const BRONZE_LIGHT = palette.bronzeLight
const TERTIARY = '$tertiary'
const WHITE_DIM = 'rgba(255,255,255,0.7)'
const WHITE_TRACK = 'rgba(255,255,255,0.18)'

/** Course view-model over the live `/learning/courses` payload. */
interface CourseVM {
  id: string
  title: Localized
  cat: Localized
  lessons: number
  hours: number
  tone: Tone
  progress: number
  enrolled: boolean
}

const lessonsDone = (c: CourseVM) => Math.round((c.progress / 100) * c.lessons)
const catKey = (c: Localized): string => (typeof c === 'string' ? c : c.ru)
/** One-lesson progress step, used by the Continue action. */
const progressStep = (c: CourseVM) => Math.min(100, c.progress + Math.max(1, Math.round(100 / c.lessons)))

// ── Stats strip ───────────────────────────────────────────────────────────
function StatCard({ icon, toneKey, value, label, index }: { icon: string; toneKey: string; value: number; label: string; index: number }) {
  const tn = tone(toneKey)
  return (
    <Enter index={index} flexGrow={1} flexBasis={160} minWidth={140}>
      <Card flexGrow={1} padding={16}>
      <XStack gap={12} alignItems="center">
        <Stack width={40} height={40} borderRadius={9} backgroundColor={tn.soft} alignItems="center" justifyContent="center">
          <Icon name={icon} size={20} color={tn.text} />
        </Stack>
        <YStack>
          <Text fontSize={22} fontWeight="800" color="$color" letterSpacing={-0.4}>
            {value}
          </Text>
          <Text fontSize={12} color="$muted">
            {label}
          </Text>
        </YStack>
      </XStack>
      </Card>
    </Enter>
  )
}

// ── Continue-learning hero (navy + bronze ring) ─────────────────────────────
function ContinueHero({ c, busy, onContinue }: { c: CourseVM; busy?: boolean; onContinue: () => void }) {
  const { t, l } = useApp()
  const { isPhone } = useResponsive()
  return (
    <Card overflow="hidden" padding={0}>
      <Stack backgroundColor={NAVY} padding={24} overflow="hidden">
        {/* brand bloom + brushed-bronze bottom edge (login/profile motif) */}
        <Stack position="absolute" top={-90} right={-40} width={240} height={240} borderRadius={999} backgroundColor={BLOOM} opacity={0.55} />
        <Stack position="absolute" top={10} right={90} width={120} height={120} borderRadius={999} backgroundColor={BRONZE} opacity={0.12} />
        <Stack position="absolute" bottom={0} left={0} right={0} height={3} backgroundColor={BRONZE} opacity={0.85} />

        <XStack gap={24} alignItems="center" flexDirection={isPhone ? 'column' : 'row'}>
          <YStack flex={1} gap={10} width={isPhone ? '100%' : undefined}>
            <XStack gap={7} alignItems="center">
              <Icon name="graduation-cap" size={15} color={BRONZE_LIGHT} />
              <Text fontSize={12} fontWeight="700" color={BRONZE_LIGHT} letterSpacing={0.4} textTransform="uppercase">
                {l('learn.continueLearning')}
              </Text>
            </XStack>

            <Text fontSize={20} fontWeight="800" color="#fff" letterSpacing={-0.3}>
              {t(c.title)}
            </Text>

            <XStack gap={9} alignItems="center" flexWrap="wrap">
              <XStack backgroundColor={WHITE_TRACK} borderRadius={999} paddingHorizontal={9} paddingVertical={3}>
                <Text fontSize={11} fontWeight="700" color="#fff">
                  {t(c.cat)}
                </Text>
              </XStack>
              <Text fontSize={12} color={WHITE_DIM}>
                {lessonsDone(c)}/{c.lessons} {l('learn.lessons')}
              </Text>
            </XStack>

            {/* progress bar (always visible — ring is desktop-only) */}
            <XStack alignItems="center" gap={10} marginTop={2}>
              <Stack flex={1} height={6} borderRadius={999} backgroundColor={WHITE_TRACK} overflow="hidden">
                <Stack height={6} borderRadius={999} backgroundColor={BRONZE_LIGHT} width={`${c.progress}%`} />
              </Stack>
              <Text fontSize={12} fontWeight="700" color="#fff">
                {c.progress}%
              </Text>
            </XStack>

            <XStack marginTop={6}>
              <XStack
                onPress={busy ? undefined : onContinue}
                cursor={busy ? 'default' : 'pointer'}
                animation="quick"
                height={38}
                paddingHorizontal={16}
                borderRadius={7}
                backgroundColor="#fff"
                opacity={busy ? 0.8 : 1}
                alignItems="center"
                justifyContent="center"
                gap={7}
                hoverStyle={{ opacity: 0.92 }}
                pressStyle={{ opacity: 0.85 }}
              >
                <Icon name="play" size={14} color={BLOOM} />
                <Text color={BLOOM} fontWeight="700" fontSize={13}>
                  {l('learn.continue')}
                </Text>
              </XStack>
            </XStack>
          </YStack>

          {!isPhone && (
            <Ring size={96} pct={c.progress} color={BRONZE_LIGHT} trackColor={WHITE_TRACK} thickness={7}>
              <Text fontSize={19} fontWeight="800" color="#fff">
                {c.progress}%
              </Text>
            </Ring>
          )}
        </XStack>
      </Stack>
    </Card>
  )
}

// ── Course card ─────────────────────────────────────────────────────────────
function CourseCard({
  c,
  busy,
  onStart,
  onContinue,
}: {
  c: CourseVM
  busy?: boolean
  onStart: () => void
  onContinue: () => void
}) {
  const { t, l } = useApp()
  const tn = tone(c.tone)
  const done = c.progress >= 100
  const started = c.progress > 0 && !done

  return (
    <PressCard flexGrow={1} padding={0} overflow="hidden">
      {/* tonal banner re-skinned with a navy bloom + bronze edge */}
      <Stack height={92} backgroundColor={tn.solid} alignItems="center" justifyContent="center" overflow="hidden">
        <Stack position="absolute" top={-60} right={-24} width={150} height={150} borderRadius={999} backgroundColor={BLOOM} opacity={0.35} />
        <Stack position="absolute" bottom={0} left={0} right={0} height={3} backgroundColor={BRONZE} opacity={0.8} />
        <Icon name={done ? 'award' : 'graduation-cap'} size={30} color="#fff" />
      </Stack>

      <YStack padding={16} gap={10} flex={1}>
        <Tag tone={c.tone}>{t(c.cat)}</Tag>

        <Text fontSize={14} fontWeight="700" color="$color" lineHeight={19} flex={1}>
          {t(c.title)}
        </Text>

        <XStack gap={6} alignItems="center">
          <Icon name="book-open" size={13} color={TERTIARY} />
          <Text fontSize={11} color="$muted">
            {c.lessons} {l('learn.lessons')}
          </Text>
          <Text fontSize={11} color={TERTIARY}>
            ·
          </Text>
          <Icon name="clock" size={13} color={TERTIARY} />
          <Text fontSize={11} color="$muted">
            ~{c.hours} {l('learn.hoursShort')}
          </Text>
        </XStack>

        {started && (
          <XStack alignItems="center" gap={9}>
            <Stack flex={1}>
              <ProgressBar pct={c.progress} color={tn.solid} />
            </Stack>
            <Text fontSize={11} fontWeight="700" color="$color">
              {c.progress}%
            </Text>
          </XStack>
        )}

        {done ? (
          <Button size="sm" block icon={<Icon name="award" size={14} color={tn.text} />}>
            {l('learn.completed')}
          </Button>
        ) : c.enrolled ? (
          <Button
            size="sm"
            block
            variant="primary"
            onPress={busy ? undefined : onContinue}
            opacity={busy ? 0.6 : 1}
            icon={<Icon name="play" size={13} color="#fff" />}
          >
            {l('learn.continue')}
          </Button>
        ) : (
          <Button
            size="sm"
            block
            onPress={busy ? undefined : onStart}
            opacity={busy ? 0.6 : 1}
            icon={<Icon name="play" size={13} color={BLOOM} />}
          >
            {l('learn.start')}
          </Button>
        )}
      </YStack>
    </PressCard>
  )
}

// ── Category filter chip ──────────────────────────────────────────────────────
function FilterChip({ active, label, onPress }: { active: boolean; label: string; onPress: () => void }) {
  return (
    <XStack
      onPress={onPress}
      cursor="pointer"
      animation="quicker"
      height={32}
      paddingHorizontal={13}
      borderRadius={999}
      borderWidth={1}
      alignItems="center"
      backgroundColor={active ? '$primary' : '$background'}
      borderColor={active ? '$primary' : '$borderColor'}
      hoverStyle={active ? {} : { borderColor: '$primary', backgroundColor: '$backgroundHover' }}
      pressStyle={{ scale: 0.96 }}
    >
      <Text fontSize={12} fontWeight="600" color={active ? '#fff' : '$muted'}>
        {label}
      </Text>
    </XStack>
  )
}

export default function LearningScreen() {
  const { t, l } = useApp()
  const [cat, setCat] = useState<string>('all')

  const coursesQuery = useCourses()
  const { data: liveCourses } = coursesQuery
  const { data: summary } = useLearningSummary()
  const enroll = useEnrollCourse()
  const setProgress = useSetCourseProgress()
  const busyId =
    enroll.isPending ? enroll.variables : setProgress.isPending ? setProgress.variables?.id : undefined

  const courses: CourseVM[] = (liveCourses ?? []).map((c) => ({
    id: c.id,
    title: c.title,
    cat: c.cat,
    lessons: c.lessons,
    hours: c.hours,
    tone: c.tone,
    progress: c.progress,
    enrolled: c.enrolled,
  }))

  const inProgress = useMemo(() => courses.filter((c) => c.progress > 0 && c.progress < 100), [courses])
  const completedCount = summary?.completed ?? courses.filter((c) => c.progress >= 100).length
  const inProgressCount = summary?.inProgress ?? inProgress.length
  const totalLessons = summary?.totalLessons ?? courses.reduce((sum, c) => sum + c.lessons, 0)
  const featured = useMemo(() => [...inProgress].sort((a, b) => b.progress - a.progress)[0], [inProgress])

  // unique categories in source order (stable id = ru key)
  const categories = useMemo(() => {
    const seen = new Set<string>()
    const out: Localized[] = []
    for (const c of courses) {
      const k = catKey(c.cat)
      if (!seen.has(k)) {
        seen.add(k)
        out.push(c.cat)
      }
    }
    return out
  }, [courses])

  const catalog = useMemo(
    () => (cat === 'all' ? courses : courses.filter((c) => catKey(c.cat) === cat)),
    [cat, courses],
  )

  const start = (c: CourseVM) => enroll.mutate(c.id)
  const advance = (c: CourseVM) => setProgress.mutate({ id: c.id, progress: progressStep(c) })

  return (
    <Page title={l('learn.title')} subtitle={l('learn.subtitle')}>
      {/* stats strip */}
      <XStack flexWrap="wrap" gap={12}>
        <StatCard icon="graduation-cap" toneKey="blue" value={inProgressCount} label={l('learn.inProgress')} index={0} />
        <StatCard icon="award" toneKey="green" value={completedCount} label={l('learn.completed')} index={1} />
        <StatCard icon="book-open" toneKey="gold" value={totalLessons} label={l('learn.statLessons')} index={2} />
      </XStack>

      {/* continue-learning hero */}
      {featured && (
        <Enter index={3}>
          <ContinueHero c={featured} busy={busyId === featured.id} onContinue={() => advance(featured)} />
        </Enter>
      )}

      {/* catalog */}
      <Text fontSize={15} fontWeight="700" color="$color" marginTop={4}>
        {l('learn.catalog')}
      </Text>

      <XStack flexWrap="wrap" gap={8}>
        <FilterChip active={cat === 'all'} label={l('c.all')} onPress={() => setCat('all')} />
        {categories.map((co) => {
          const k = catKey(co)
          return <FilterChip key={k} active={cat === k} label={t(co)} onPress={() => setCat(k)} />
        })}
      </XStack>

      <QueryState q={coursesQuery}>
      {catalog.length === 0 ? (
        <Card padding={48} alignItems="center">
          <Text fontSize={13} color="$muted">
            {l('learn.empty')}
          </Text>
        </Card>
      ) : (
        <XStack flexWrap="wrap" gap={16}>
          {catalog.map((c, i) => (
            // key includes the active category so switching filters re-runs the cascade
            <Enter key={`${cat}-${c.id}`} index={Math.min(i, 11)} flexGrow={1} flexBasis={240} minWidth={220} maxWidth={320}>
              <CourseCard
                c={c}
                busy={busyId === c.id}
                onStart={() => start(c)}
                onContinue={() => advance(c)}
              />
            </Enter>
          ))}
        </XStack>
      )}
      </QueryState>

      <Stack height={8} />
    </Page>
  )
}
